/**
 * The "Do" step (an Action node), v2 — its decisions, pure (no React, no
 * store), so the editor, the card and the production-corpus check make them
 * the same way.
 *
 * Storage is the old Action editor's call mode, exactly:
 *   actionData = { kind: 'call', expression: 'strcpy(name, "Ravi")', assignTo: { fullPath } | null, … }
 * and codegen writes `[assignTo = ]expression;`. A step is written only when
 * it is edited, so an unedited step generates the same code.
 *
 *   splitCall       'strcpy(name, "Ravi")' → { fn: 'strcpy', args: ['name', '"Ravi"'] }
 *   composeCall     back again, in one spelling: `fn(a, b)`
 *   doStepRoute     'v2' or 'old': which editor a stored step opens in
 *   doCallWords     the call in words ("copy "Ravi" into name")
 *   doStepCardText  what the canvas card says ("Copy "Ravi" into name")
 */
import { describeC, keyboardCanEdit, createEnv } from '@/components/visual-flow-builder/ExpressionKeyboard/engine';
import { canChipEdit } from '@/components/visual-flow-builder/ChipExpressionField/chipModel';
import { libraryEntry, libraryEnvFunctions, C_DO_LIBRARY } from './doCatalog';
import { isMemoryStep, memoryStepHolds, storedMemory, memoryWords, memoryJobRefusal, MEMORY_FNS } from './memoryStep';

/** The guided Memory and File forms: still the old editor's (Ram, 2026-10-24). */
export const GUIDED_KINDS = ['allocate', 'resize', 'free', 'file'];

/**
 * A stored call split into its function and its values, each value exactly as
 * written. null when the text is not ONE call (`k = k + 1`, `(void) b`,
 * `f(x) + 1`, `a(); b()`).
 */
export function splitCall(text) {
  const s = String(text || '').trim();
  const m = /^([A-Za-z_]\w*)\s*\(/.exec(s);
  if (!m || !s.endsWith(')')) return null;
  const open = m[0].length - 1;
  const args = [];
  let depth = 0;
  let quote = null;
  let start = open + 1;
  for (let i = open; i < s.length; i += 1) {
    const ch = s[i];
    if (quote) {
      if (ch === '\\') { i += 1; continue; } // eslint-disable-line no-continue
      if (ch === quote) quote = null;
      continue; // eslint-disable-line no-continue
    }
    if (ch === '"' || ch === "'") { quote = ch; continue; } // eslint-disable-line no-continue
    if (ch === '(' || ch === '[' || ch === '{') depth += 1;
    else if (ch === ')' || ch === ']' || ch === '}') {
      depth -= 1;
      if (depth === 0) {
        // The call's own `)` must be the last character: `f(x) + 1` is not one call.
        if (i !== s.length - 1) return null;
        args.push(s.slice(start, i).trim());
      }
      if (depth < 0) return null;
    } else if (ch === ',' && depth === 1) {
      args.push(s.slice(start, i).trim());
      start = i + 1;
    } else if (ch === ';') return null;
  }
  if (depth !== 0 || quote) return null;
  if (args.length === 1 && args[0] === '') args.length = 0;
  return { fn: m[1], args };
}

/** The call as C: `strcpy(name, "Ravi")`. */
export const composeCall = (fn, args = []) => `${fn}(${(args || []).map((a) => String(a || '').trim()).join(', ')})`;

/**
 * The parameters a Do step shows for a function: the library's, a flow's
 * (`ownFlows`: [{ name, params: [{ name, type }] , returns }]), else null.
 */
export function entryFor(fn, ownEntries = []) {
  return (ownEntries || []).find((e) => e && e.fn === fn) || libraryEntry(fn);
}

/**
 * Which editor a stored Do step opens in: 'v2', or 'old' for what the chips
 * cannot hold without changing it —
 *   - the guided Memory / File forms (kind allocate / resize / free / file);
 *   - the legacy operation format (no expression, an operationId);
 *   - a statement, not one call (`k = k + 1`, `x = fopen(…)`, `(void) b`);
 *   - a call to a function Do does not know (`va_start`), or with more values
 *     than it takes;
 *   - a value the chips cannot hold exactly (canChipEdit false);
 *   - a result kept somewhere that is not a plain place.
 * An empty step is v2's.
 * → { route: 'v2' | 'old', reason }
 */
export function doStepRoute(node, { env = createEnv(), ownEntries = [] } = {}) {
  const ad = (node && node.actionData) || {};
  // The old guided File form stored its plain call too: it opens as a File step
  // (Ram, 2026-10-27). Memory's guided forms keep the old editor.
  const guidedFile = ad.kind === 'file' && splitCall(ad.expression || '');
  if (GUIDED_KINDS.includes(ad.kind) && !guidedFile) return { route: 'old', reason: 'guided' };
  const expr = String(ad.expression || '');
  if (!expr.trim()) {
    if (node && (node.operationId || node.operationData)) return { route: 'old', reason: 'legacy' };
    return { route: 'v2', reason: 'empty' };
  }
  if (!keyboardCanEdit(expr)) return { route: 'old', reason: 'statement' };
  const call = splitCall(expr);
  if (!call) return { route: 'old', reason: 'statement' };
  const e = entryFor(call.fn, ownEntries);
  if (!e) return { route: 'old', reason: 'unknown-function' };
  if (call.args.length > e.params.length && !e.variadic) return { route: 'old', reason: 'too-many' };
  if (call.args.some((a) => !a || !canChipEdit(a, env))) return { route: 'old', reason: 'chips' };
  const keep = String((ad.assignTo && ad.assignTo.fullPath) || '').trim();
  if (keep && !canChipEdit(keep, env)) return { route: 'old', reason: 'keep' };
  return { route: 'v2', reason: 'call' };
}

/** An env that knows the library's calls, whatever has loaded. */
export function withLibrary(env) {
  const base = env || createEnv();
  return { ...base, functions: { ...libraryEnvFunctions(), ...(base.functions || {}) } };
}

/** The call in words: "copy "Ravi" into name"; '' when it does not read. */
export function doCallWords(expression, env = createEnv()) {
  const s = String(expression || '').trim();
  if (!s) return '';
  try {
    const d = describeC(s, withLibrary(env), { sequence: true });
    return d.err ? '' : (d.words || '');
  } catch (e) {
    return '';
  }
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * What the canvas card says for a v2 Do step: the call in words, capitalised
 * ("Copy "Ravi" into name"); a kept result: "Set len to the length of name".
 * '' when there is no call yet.
 */
export function doStepCardText(node, env = createEnv()) {
  const ad = (node && node.actionData) || {};
  // A Memory step says its job in words: "Get room for n whole numbers → p".
  if (isMemoryStep(node) && memoryStepHolds(node)) return memoryWords(storedMemory(node));
  const expr = String(ad.expression || '').trim();
  if (!expr) {
    const chosen = ad.functionName ? libraryEntry(ad.functionName) : null;
    return chosen ? chosen.label : (ad.functionName || '');
  }
  if (!keyboardCanEdit(expr)) return '';   // a statement (`k = k + 1`): the card keeps its own name
  const words = doCallWords(expr, env) || expr;
  const keep = String((ad.assignTo && ad.assignTo.fullPath) || '').trim();
  return keep ? `Set ${keep} to ${words}` : cap(words);
}

/**
 * The label the OLD editor stamps on its first save: `strcpy()`, `free()`, or
 * the first 20 characters of the expression. Such a label was never chosen by
 * the student.
 */
export function isOldStampedLabel(label, node) {
  const l = String(label || '').trim();
  if (!l) return true;
  if (/^[A-Za-z_]\w*\(\)$/.test(l)) return true;
  const expr = String((node && node.actionData && node.actionData.expression) || '');
  return !!expr && l === expr.substring(0, 20);
}

// ── the File step (Ram, 2026-10-27) ─────────────────────────────────────────
// A File step is a Do step that does a file job: stored exactly as Do stores
// it (so codegen, Code Next and every existing file item are unchanged), shown
// as its own step — "File" in the + menu, its own card, its own editor.

const FILE_FNS = new Set(C_DO_LIBRARY.filter((e) => e.group === 'Files').map((e) => e.fn));

/** A file job's function name. */
export const isFileFunction = (fn) => FILE_FNS.has(String(fn || ''));

/** A Do step made as a File step, or holding a file job. */
export function isFileStep(node) {
  if (!node || node.type !== 'action') return false;
  const ad = node.actionData || {};
  if (ad.stepKind === 'file') return true;
  const call = splitCall(ad.expression || '');
  return isFileFunction((call && call.fn) || ad.functionName);
}

/** The jobs the File step shows as buttons, in the order a file is used. */
export const FILE_MAIN_JOBS = ['fopen', 'fgets', 'fscanf', 'fputs', 'fprintf', 'fclose'];

/** The + menu / panel header words. */
export const FILE_STEP_WORDS = { label: 'File', description: 'Open, read, write or close a file' };

/**
 * A file job written into another step's value (a Set): refused in plain words
 * — the right step for the job is File (Ram, 2026-10-27). null when there is none.
 */
export function fileJobRefusal(expr) {
  const m = /\b(fopen|fclose|fgets|fgetc|fscanf|fread|fputs|fputc|fprintf|fwrite|feof|fseek|ftell|rewind|remove|rename)\s*\(/.exec(String(expr || ''));
  if (!m) return null;
  const fn = m[1];
  if (fn === 'fopen') return 'Opening a file is its own step — use File → Open.';
  if (fn === 'fclose') return 'Closing a file is its own step — use File → Close.';
  if (['fgets', 'fgetc', 'fscanf', 'fread'].includes(fn)) return 'Reading from a file is its own step — use File → Read.';
  if (['fputs', 'fputc', 'fprintf', 'fwrite'].includes(fn)) return 'Writing to a file is its own step — use File → Write.';
  return 'Working with a file is its own step — use a File step.';
}

/** A keyboard env without the file jobs (they belong to the File step). */
export function withoutFileJobs(env) {
  if (!env || !env.functions) return env;
  const functions = Object.fromEntries(Object.entries(env.functions).filter(([n]) => !isFileFunction(n)));
  return { ...env, functions };
}

/** A file or memory job in another step's value, refused in words (the right step for the job). */
export const stepJobRefusal = (expr) => fileJobRefusal(expr) || memoryJobRefusal(expr);

/** A keyboard env without the file and memory jobs (they have their own steps). */
export function withoutStepJobs(env) {
  const e = withoutFileJobs(env);
  if (!e || !e.functions) return e;
  const functions = Object.fromEntries(Object.entries(e.functions).filter(([n]) => !MEMORY_FNS.includes(n)));
  return { ...e, functions };
}
